/* eslint-disable max-lines */
import { useAtom, useAtomValue } from "jotai";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "tailwind-variants";

import {
  mobileSizePlacementOpenAtom,
  refineArtworkOpenAtom,
  useArtworkForm,
  usePlacementPosition
} from "@design-lab/entities/artwork";
import { useFormattedDeliveryDate } from "@design-lab/entities/composition/model";
import { hasRequestedDesignServicesAtom, pinningModeAtom } from "@design-lab/entities/pinning";
import { DesignArtwork } from "@design-lab/features/design-artwork";
import { PlacementControlTooltip } from "@design-lab/features/design-artwork/ui/PlacementControlTooltip";
import { ReviewPinningModal } from "@design-lab/features/manage-cart";
import { ManageProductSizing } from "@design-lab/features/manage-product-sizing/ui/ManageProductSizing";
import { SaveProject } from "@design-lab/features/save-project";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { SelectProductColor } from "@design-lab/features/select-product-color";
import {
  useRecapture,
  UserRetentionModal,
  useUserRetention
} from "@design-lab/features/user-retention";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import type { AppTab, Tab } from "@design-lab/shared/types/tab";
import { Button } from "@design-lab/shared/ui";
import { FrontBackSubTabs } from "@design-lab/shared/ui/layouts/FrontBackSubTabs";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { UploadSection } from "@design-lab/shared/ui/layouts/UploadSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@design-lab/shared/ui/tabs";
import { DesignLabArtboard } from "@design-lab/widgets";
import { PinningArtboard } from "@design-lab/widgets/artboard/ui/pinning-mode";
import { ImagePlacement, ImageSize } from "@design-lab/widgets/design-lab-upload-artwork";
import { AddToCart } from "@design-lab/widgets/footer-desktop/ui/AddToCart";
import { Header } from "@design-lab/widgets/navigation";
import { NextButton } from "@design-lab/widgets/navigation/ui/NextButton";

const EXPANDED_HEIGHT = 40; // vh
const COLLAPSED_HEIGHT = 38; // px

export function DesignLabMobile({
  handleTabClick,
  tabs,
  tab,
  setTab
}: {
  handleTabClick: (value: Tab) => void;
  tabs: AppTab[];
  tab: Tab;
  setTab: (value: Tab) => void;
}) {
  const { lens } = useArtworkForm();
  const currentPlacementLens = lens.focus("currentPlacement");
  const { placement } = usePlacementPosition(currentPlacementLens);
  const { canGoNext } = useTab();

  const { isSaveProjectVisible, setIsSaveProjectVisible } = useSaveProject();

  const placementLens = lens.focus(placement);

  const side = placement === "back" ? "back" : "front";

  const deliveryDateInfo = useFormattedDeliveryDate();
  const drawerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevTabRef = useRef(tab);
  const { currentIndex } = useTabNavigation();
  const [isSizePlacementOpen, setIsSizePlacementOpen] = useAtom(mobileSizePlacementOpenAtom);

  const [isExpanded, setIsExpanded] = useState(true);
  const isPinningMode = useAtomValue(pinningModeAtom);
  const isRefineOpen = useAtomValue(refineArtworkOpenAtom);
  const hasRequestedDesignServices = useAtomValue(hasRequestedDesignServicesAtom);

  const { isArtworkEnhanced, isSomeArtworkLoading } = useValidation();
  const { onRecaptured } = useRecapture({ setTab, isSaveProjectVisible, setIsSaveProjectVisible });

  const { isModalOpen, closeModal, handleRecaptured } = useUserRetention({
    hasArtwork: isArtworkEnhanced,
    isSomeArtworkLoading,
    hasRequestedDesignServices,
    exitIntentEnabled: false,
    onRecaptured,
    enabled: !isPinningMode && !isRefineOpen
  });

  const handleTabHeaderClick = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (prevTabRef.current !== tab) {
      setIsExpanded(true);
      setIsSizePlacementOpen(false);
    }
    prevTabRef.current = tab;
  }, [tab, setIsSizePlacementOpen, setIsExpanded]);

  const currentHeight = isExpanded ? `${EXPANDED_HEIGHT}vh` : `${COLLAPSED_HEIGHT}px`;

  // When pinning mode is active, render the full-screen pinning UI
  if (isPinningMode) {
    return <PinningArtboard />;
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-gray-100">
      <ReviewPinningModal />
      <Header deliveryDateInfo={deliveryDateInfo} />
      <UserRetentionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onRequestEdits={handleRecaptured}
      />
      <Tabs value={tab} className="relative flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 p-1 pb-0">
          <DesignLabArtboard />
        </div>

        {isSizePlacementOpen ? (
          <div className="bg-background mx-1 space-y-4 rounded-t-2xl p-2 shadow-lg">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Size & Placement</p>
              <Button
                data-testid="close-size-placement-button"
                variant="icon"
                className="p-2"
                onPress={() => setIsSizePlacementOpen(false)}
              >
                <X className="size-6" />
              </Button>
            </div>
            <div className="flex items-end gap-6">
              <div className="space-y-2">
                <p className="text-sm font-semibold">Design Size</p>
                <ImageSize lens={placementLens.focus("size")} side={side} />
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center">
                  <p className="text-sm font-semibold">Placement</p>
                  <PlacementControlTooltip isDesktop={false} />
                </div>
                <ImagePlacement />
              </div>
            </div>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="bg-background mx-1 flex flex-col overflow-hidden rounded-t-2xl border p-2 pt-0 transition-[height] duration-300 ease-out"
            style={{
              height: currentHeight,
              minHeight: `${COLLAPSED_HEIGHT}px`,
              maxHeight: `${EXPANDED_HEIGHT}vh`
            }}
          >
            <div
              className="flex h-9 items-center justify-between gap-2"
              onClick={handleTabHeaderClick}
              data-testid="mobile-tab-header"
            >
              <p className="text-sm font-medium">
                {currentIndex + 1}. {tabs[currentIndex]?.mobileAccordionTitle}
              </p>
              <Button
                variant="icon"
                onPress={handleTabHeaderClick}
                aria-label="Close"
                className="p-0"
                data-testid="close-expanded-tabs-button"
              >
                {isExpanded ? <ChevronDown className="size-6" /> : <ChevronUp className="size-6" />}
              </Button>
            </div>

            <div
              ref={drawerRef}
              className={cn(
                "transition-height h-0 overflow-y-auto duration-300 ease-out",
                isExpanded && "h-full"
              )}
            >
              <TabsContent value="upload" className="mt-0 h-full">
                <UploadSection />
              </TabsContent>
              <TabsContent value="products" className="mt-0 h-full">
                <SelectProductColor />
              </TabsContent>
              <TabsContent value="size" className="mt-0 h-full">
                <ManageProductSizing />
              </TabsContent>
              <TabsContent value="front" className="mt-0 h-full">
                <DesignArtwork side="front" title="4. Design Front" />
              </TabsContent>
              <TabsContent value="back" className="mt-0 h-full">
                <DesignArtwork side="back" title="5. Design Back" />
              </TabsContent>
              <TabsContent value="design" className="mt-0 h-full">
                <FrontBackSubTabs
                  title="Design"
                  number={2}
                  testIdPrefix="design"
                  frontContent={<DesignArtwork side="front" title="" />}
                  backContent={<DesignArtwork side="back" title="" />}
                />
              </TabsContent>
            </div>
            <NextButton className="h-12 w-full bg-blue-700 hover:bg-blue-800" />
          </div>
        )}

        {!canGoNext && (
          <div className="flex flex-col gap-2 px-2 py-1">
            <AddToCart />
          </div>
        )}

        <TabsList
          data-testid="design-lab-mobile-tablist"
          className="flex-0 z-30 h-auto flex-row gap-1"
        >
          {tabs.map((item, index) => (
            <TabsTrigger
              data-testid={`tab-${item.value}-trigger`}
              key={item.title}
              value={item.value}
              asChild
              disabled={!item.enabled}
            >
              <Button
                data-testid={`tab-${item.value}-button`}
                variant="secondary"
                isDisabled={!item.enabled}
                onPress={() => {
                  if (tab === item.value) {
                    setIsExpanded(!isExpanded);
                  } else {
                    handleTabClick(item.value);
                  }
                }}
                className={
                  cn(
                    "bg-background relative h-auto w-full flex-col gap-1 border px-2 text-xs shadow-md",
                    isExpanded &&
                      "group-data-[selected]:border-blue-600 group-data-[selected]:text-blue-600"
                  )!
                }
              >
                {item.icon}
                {item.enabled && item.checked && (
                  <div className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-blue-600">
                    <Check className="text-background h-3 w-3" />
                  </div>
                )}
                <span>
                  {index + 1}. {item.mobileTitle}
                </span>
              </Button>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isSaveProjectVisible && <SaveProject />}
    </div>
  );
}
